<div class="student-card p-3 p-md-4">

  <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-3">
    <p class="fs-20 fw-800 color-gray-950 mb-0">
      <i class="bi bi-clipboard-check color-brand-primary me-2"></i>{{'students.tasks.title'|translate}}
    </p>

    <div class="d-flex align-items-center mt-2 mt-lg-0" *ngIf="totalCount">
      <span class="fs-small fw-400 color-gray-400 me-2">
        {{'students.tasks.progress'|translate: {progress: progressLabel()} }}
      </span>
      <div class="progress-track">
        <div class="progress-value" [style.width.%]="progressPercent()"></div>
      </div>
    </div>
  </div>

  <!-- New task -->
  <form [formGroup]="fg" (ngSubmit)="onSubmit()" class="d-flex align-items-start mb-3">
    <div class="flex-grow-1 me-2">
      <app-gray-text-input
        customClass="compact-input"
        [hasErr]="isSubmitted && fg.get('content')?.invalid"
        [placeholder]="'students.tasks.placeholder'|translate"
        formControlName="content"></app-gray-text-input>
    </div>

    <app-gray-button
      type="submit" color="primary" [fullWidth]="false" padding="px-3 py-2 gap-1" [weight]="800"
      customClass="d-flex align-items-center fs-small border-8" preIcon="plus"
      [isLoading]="isSaving" [disabled]="isSaving"
      [text]="'students.tasks.add'|translate"></app-gray-button>
  </form>

  <!-- Filters -->
  <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
    <div class="filter-switch d-inline-flex">
      <a class="filter-option text-center fs-small fw-800 clickable px-3 py-1"
         [class.active]="filter === 'all'" (click)="setFilter('all')">
        {{'students.tasks.filter_all'|translate}}
      </a>
      <a class="filter-option text-center fs-small fw-800 clickable px-3 py-1"
         [class.active]="filter === 'open'" (click)="setFilter('open')">
        {{'students.tasks.filter_open'|translate}}
      </a>
      <a class="filter-option text-center fs-small fw-800 clickable px-3 py-1"
         [class.active]="filter === 'completed'" (click)="setFilter('completed')">
        {{'students.tasks.filter_completed'|translate}}
      </a>
    </div>

    <div class="search-box mt-2 mt-md-0">
      <app-gray-text-input
        customClass="compact-input"
        [placeholder]="'students.tasks.search'|translate"
        [formControl]="searchControl"></app-gray-text-input>
    </div>
  </div>

  <ngx-skeleton-loader
    *ngIf="isLoading && !tasks.length" count="4" appearance="line"
    [theme]="{height: '56px', 'border-radius': '12px', margin: '0 0 8px 0'}"/>

  <!-- Tasks -->
  <div class="task-row d-flex align-items-center p-3 mb-2" *ngFor="let task of tasks"
       [class.completed]="task.isCompleted">

    <a class="task-check clickable me-3" (click)="toggleTask(task)"
       [ngbTooltip]="(task.isCompleted ? 'students.tasks.mark_open' : 'students.tasks.mark_done')|translate"
       container="body">
      <i class="bi" [class.bi-check-circle-fill]="task.isCompleted" [class.bi-circle]="!task.isCompleted"></i>
    </a>

    <div class="flex-grow-1 overflow-hidden">
      <ng-container *ngIf="editingTaskId !== task.id">
        <p class="fs-small fw-500 color-gray-950 mb-0 task-content">{{task.content}}</p>
        <p class="fs-xsmall fw-400 color-gray-400 mb-0"
           *ngIf="task.isCompleted && task.completedBySource === actorTypes.Student">
          {{'students.tasks.completed_by_student'|translate: {date: task.completedAt | date: 'd MMMM'} }}
        </p>
      </ng-container>

      <ng-container *ngIf="editingTaskId === task.id">
        <app-gray-text-input customClass="compact-input" [formControl]="editControl"></app-gray-text-input>

        <div class="d-flex justify-content-end mt-2">
          <app-gray-button
            color="stroke" [fullWidth]="false" padding="py-1 px-3" [weight]="500"
            customClass="fs-small me-2"
            [text]="'buttons.give_up'|translate" (click)="cancelEdit()"></app-gray-button>

          <app-gray-button
            color="primary" [fullWidth]="false" padding="py-1 px-3" [weight]="800" customClass="fs-small"
            [isLoading]="editIsSaving" [disabled]="editIsSaving"
            [text]="'buttons.save'|translate" (click)="saveEdit(task)"></app-gray-button>
        </div>
      </ng-container>
    </div>

    <div class="d-flex align-items-center ms-2" *ngIf="editingTaskId !== task.id">
      <span class="source-badge fs-xsmall fw-800 me-2" *ngIf="sourceLabelKey(task)">
        {{sourceLabelKey(task)|translate}}
      </span>

      <a class="task-action clickable color-gray-400 ms-2" (click)="startEdit(task)"
         [ngbTooltip]="'buttons.edit'|translate" container="body">
        <i class="bi bi-pencil"></i>
      </a>
      <a class="task-action clickable color-gray-400 ms-2" (click)="confirmDelete(task)"
         [ngbTooltip]="'buttons.remove'|translate" container="body">
        <i class="bi bi-trash"></i>
      </a>
    </div>
  </div>

  <div class="text-center py-4" *ngIf="!isLoading && !tasks.length">
    <p class="fs-small fw-800 color-gray-950 mb-1">{{'students.tasks.empty_title'|translate}}</p>
    <p class="fs-small fw-400 color-gray-400 mb-0">{{'students.tasks.empty_desc'|translate}}</p>
  </div>

  <app-gray-button
    class="d-block mt-3" *ngIf="hasMore()"
    color="stroke" [weight]="500" padding="py-2" customClass="fs-small"
    [isLoading]="isLoading" [disabled]="isLoading"
    [text]="'students.tasks.load_more'|translate" (click)="loadMore()"></app-gray-button>

  <p class="fs-small fw-400 color-gray-500 mb-0 mt-3">
    <i class="bi bi-info-circle me-1"></i>{{'students.tasks.student_note'|translate}}
  </p>

</div>
